import { useCallback, useEffect, useRef, useState } from 'react';
import { settleExamRequest } from '@/utils/network/examRequest';

export type ExamLoadingStatus = 'loading' | 'retrying' | 'ready';

type UseExamLoadingOptions = {
  recordId: number;
  load: (signal: AbortSignal) => Promise<void>;
};

const RETRY_DELAYS_MS = [1_000, 2_000, 4_000, 8_000];

export function useExamLoading({recordId, load}: UseExamLoadingOptions) {
  const [status, setStatus] = useState<ExamLoadingStatus>('loading');
  const recordIdRef = useRef(recordId);
  const loadRef = useRef(load);
  const readyRecordIdRef = useRef<number | null>(null);
  const activeRequestRef = useRef<AbortController | null>(null);
  const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const attemptsRef = useRef(0);

  loadRef.current = load;

  const clearRetryTimer = useCallback(() => {
    if (retryTimerRef.current !== null) {
      clearTimeout(retryTimerRef.current);
      retryTimerRef.current = null;
    }
  }, []);

  const start = useCallback((retrying = false) => {
    const currentRecordId = recordIdRef.current;
    if (readyRecordIdRef.current === currentRecordId || activeRequestRef.current) return;

    clearRetryTimer();
    const controller = new AbortController();
    activeRequestRef.current = controller;
    setStatus(retrying ? 'retrying' : 'loading');

    void settleExamRequest(requestSignal => loadRef.current(requestSignal), undefined, controller.signal)
      .then(() => {
        if (controller.signal.aborted || activeRequestRef.current !== controller) return;
        activeRequestRef.current = null;
        readyRecordIdRef.current = currentRecordId;
        attemptsRef.current = 0;
        setStatus('ready');
      })
      .catch(() => {
        if (controller.signal.aborted || activeRequestRef.current !== controller) return;
        activeRequestRef.current = null;
        attemptsRef.current += 1;
        setStatus('retrying');
        const delay = RETRY_DELAYS_MS[Math.min(attemptsRef.current - 1, RETRY_DELAYS_MS.length - 1)];
        retryTimerRef.current = setTimeout(() => {
          retryTimerRef.current = null;
          start(true);
        }, delay);
      });
  }, [clearRetryTimer]);

  useEffect(() => {
    recordIdRef.current = recordId;
    readyRecordIdRef.current = null;
    attemptsRef.current = 0;
    clearRetryTimer();
    activeRequestRef.current?.abort();
    activeRequestRef.current = null;
    start();

    const onOnline = () => {
      if (readyRecordIdRef.current === recordIdRef.current || activeRequestRef.current) return;
      clearRetryTimer();
      start(true);
    };
    window.addEventListener('online', onOnline);
    return () => {
      window.removeEventListener('online', onOnline);
      clearRetryTimer();
      activeRequestRef.current?.abort();
      activeRequestRef.current = null;
    };
  }, [recordId, clearRetryTimer, start]);

  const retry = useCallback(() => {
    if (activeRequestRef.current || readyRecordIdRef.current === recordIdRef.current) return;
    clearRetryTimer();
    start(true);
  }, [clearRetryTimer, start]);

  return {
    ready: status === 'ready' && readyRecordIdRef.current === recordId,
    status,
    retry,
  };
}
